// Rewrite a configured fixed-host API base URL to the SPA's OWN origin (keeping the path) in a deployed
// multi-tenant environment. The SPA is served per-tenant (e.g. https://acme.dev.logikspark.ai); ALL of its
// API calls must stay same-origin so (a) the tenant subdomain is preserved for the server-side host
// resolution (X-Forwarded-Host) and (b) there is no cross-origin CORS. Front Door routes
// *.dev.logikspark.ai/api/<svc>/* to each backend, and the Function Apps deliberately do NOT list the
// per-tenant wildcard as a CORS origin (App Service CORS has no wildcard-subdomain syntax) — same-origin is
// the intended mechanism, not CORS. On localhost there is no meaningful tenant subdomain, so the configured
// base (the CRA proxy path, or a direct-to-FA URL) is respected unchanged; a same-origin rewrite there
// would point at the dev server, which doesn't mount /api.
const sameOrigin = (configured: string | undefined): string | undefined => {
  if (!configured) {
    return configured
  }
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return configured
  }
  const path = new URL(configured, window.location.origin).pathname
  return `${window.location.origin}${path}`
}

// Every microservice base URL is forced to the SPA's own origin (see sameOrigin). The fixed
// app.dev.logikspark.ai host in the env is only the source of the /api/<svc>/ PATH — the host is replaced
// with the current tenant origin so authenticated calls (personas, orders, assets, core, products, …) don't
// cross-origin CORS-fail from a tenant subdomain. Types are preserved from the raw env reads below.
export const LOGIKSPARK_ORDERS_API_URL = sameOrigin(process.env.REACT_APP_LOGIKSPARK_ORDERS_API_URL) || ''
export const LOGIKSPARK_USERS_API_URL = sameOrigin(process.env.REACT_APP_LOGIKSPARK_USERS_API_URL)
export const LOGIKSPARK_ASSETS_API_URL = sameOrigin(process.env.REACT_APP_LOGIKSPARK_ASSETS_API_URL)
export const LOGIKSPARK_CORE_API_URL = sameOrigin(process.env.REACT_APP_LOGIKSPARK_CORES_API_URL) || ''
export const LOGIKSPARK_PRODUCTS_API_URL = sameOrigin(process.env.REACT_APP_LOGIKSPARK_PRODUCTS_API_URL) || ''

// Pre-auth callers (identity-config, public branding) use this; the USERS base above is already rewritten
// to the SPA origin, so it's now a straight accessor that defaults the path when the env is unset.
export const sameOriginUsersApiUrl = (): string => LOGIKSPARK_USERS_API_URL || '/api/users/'

// Public base URL of the branding blob container (the E8/WI 565 public account). The pre-auth /signup
// flow reads its fixed platform brand assets (logo, background) from under the `platform/` prefix here.
// Per-env value; blank when unset, which the signup layout tolerates (a broken <img> is non-fatal).
export const LOGIKSPARK_BRANDING_BASE_URL = process.env.REACT_APP_LOGIKSPARK_BRANDING_BASE_URL || ''

export const REDIRECT_URL = process.env.REACT_APP_LOGIKSPARK_REDIRECT_URL
export const AUTHORITY = process.env.REACT_APP_LOGIKSPARK_AUTHORITY
export const AUTHORITY_DOMAIN = process.env.REACT_APP_LOGIKSPARK_AUTHORITY_DOMAIN || ''
export const CLIENT_ID = process.env.REACT_APP_LOGIKSPARK_CLIENT_ID || ''
export const AUTH_DISABLED = AUTHORITY === 'disabled'
export const TRANSPORT_FEATURE = process.env.REACT_APP_TRANSPORT_FEATURE === 'true'
export const LOG_LEVEL = process.env.REACT_APP_LOG_LEVEL || 'error'

// E8 — the sessionStorage key holding the active persona's User id, sent as the X-LogikSpark-Persona-Id
// header. Defined in this shared axios/const layer so its single definition is importable by BOTH the
// request interceptor (app/axios/logikspark.ts) and the persona feature (features/Auth/personaStorage.ts)
// without a layering cycle.
export const ACTIVE_PERSONA_USER_ID_KEY = 'lsk.activePersona.userId'

// E11 R3 (WI 605) — the sessionStorage key holding the active impersonation session Id, sent as the
// X-LogikSpark-Impersonation-Session-Id header (the E9 Stage-4 middleware validates it against the persona's
// active session). Same shared-const home + reason as the persona key: importable by both the interceptor
// and the impersonation feature (features/CommonSettings/Impersonation/impersonationSessionStorage.ts)
// without a layering cycle. Absent unless a session is live.
export const IMPERSONATION_SESSION_ID_KEY = 'lsk.impersonation.sessionId'
